import { menuItems as menuBarItems } from '../templates/baseApp/components/menuBar/menuItems';
import { menuItems as bottomMenuItems } from '../templates/baseApp/components/bottomMenu/menuItems';

/**
 * Rotas que nunca são bloqueadas (navegação básica / saída).
 * `/` e `/minha-area` são o destino de fallback quando o acesso é negado.
 */
const ALWAYS_ALLOWED = [
  '/',
  '/minha-area',
  '/perfil',
  '/logout',
  '/onboarding',
  '/redireciona',
  '/favoritos',
  '/comunidade',
  '/referidos'
];

/**
 * Páginas de listagem/navegação sempre acessíveis, mesmo sem acesso aos
 * itens em si. Servem para o usuário navegar e ver os cards (os que ele
 * não tem acesso aparecem borrados com upsell).
 */
const NAV_ALLOWED = ['/cursos/regulares', '/cursos/complementares'];

/** Reduz um path para o seu primeiro segmento: `/cursos/regulares/x` -> `/cursos`. */
export const baseSegment = (path?: string): string => {
  if (!path || path === '/') return '/';
  const clean = path.split('?')[0].split('#')[0];
  const seg = clean.split('/').filter(Boolean)[0];
  return seg ? `/${seg}` : '/';
};

/** Remove query/hash e a barra final, para comparar caminhos. */
const normalizePath = (path: string): string => {
  const clean = path.split('?')[0].split('#')[0];
  return clean.length > 1 && clean.endsWith('/') ? clean.slice(0, -1) : clean;
};

/**
 * true se `pathname` está coberto por um href liberado `granted`.
 * Cobre o próprio href e qualquer subrota:
 *  - granted `/cursos` cobre `/cursos`, `/cursos/regulares`, `/cursos/curso-x`...
 *  - granted `/cursos/curso-x` cobre só `/cursos/curso-x` e `/cursos/curso-x/aulas`.
 */
const isCoveredBy = (pathname: string, granted: string): boolean => {
  const p = normalizePath(pathname);
  const g = normalizePath(granted);
  return p === g || p.startsWith(`${g}/`);
};

/**
 * Catálogo de áreas "protegíveis", derivado do arquivo de itens de menu.
 * O menu é a fonte única: cada href/link vira uma área candidata a bloqueio.
 */
export const getProtectableAreas = (): string[] => {
  const hrefs = new Set<string>();

  [...menuBarItems, ...bottomMenuItems].forEach((item) => {
    if (item.href) hrefs.add(baseSegment(item.href));
    item.items?.forEach((sub) => {
      if (sub.link && !sub.externalLink) hrefs.add(baseSegment(sub.link));
    });
  });

  return Array.from(hrefs);
};

/** Devolve a área protegível correspondente ao path, ou null se não for catalogada. */
export const getAreaForPath = (pathname: string): string | null => {
  const base = baseSegment(pathname);
  return getProtectableAreas().includes(base) ? base : null;
};

/**
 * Decide se o usuário pode acessar a rota.
 *
 * `platformModules` é a lista de hrefs liberados que vem do backend
 * (ex.: ['/cursos', '/disciplinas']). Enquanto for null/undefined
 * (backend ainda não enviou), nada é bloqueado.
 */
export const isRouteAllowed = (
  pathname: string,
  platformModules?: string[] | null
): boolean => {
  // Sem dado do backend -> não bloqueia (evita trancar usuários).
  if (!platformModules) return true;

  const base = baseSegment(pathname);
  if (ALWAYS_ALLOWED.includes(base)) return true;

  // Páginas de listagem/navegação são sempre acessíveis.
  if (NAV_ALLOWED.some((nav) => isCoveredBy(pathname, nav))) return true;

  const area = getAreaForPath(pathname);
  // Rota fora do catálogo do menu -> não é uma área controlada.
  if (!area) return true;

  // Match por prefixo: aceita tanto liberação da área inteira (/cursos)
  // quanto de um item específico (/cursos/curso-aprova-enem-2026).
  return platformModules.some((granted) => isCoveredBy(pathname, granted));
};
